import { useState, useEffect } from 'react'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { formatFullThaiDate, toISODateString } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

type Status = 'present' | 'absent' | 'leave'

interface StudentRecord {
  studentId: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  status: Status | null
}

export function AttendancePage() {
  const today = toISODateString(new Date())
  const [date] = useState(today)
  const [records, setRecords] = useState<StudentRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaved, setIsSaved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSendingNotify, setIsSendingNotify] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    fetchAttendance()
  }, [date])

  async function fetchAttendance() {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl(`/api/attendance?date=${date}`), { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setRecords(data.data.students)
        // If any records exist, we're in "already saved" state
        if (data.data.summary.checked > 0) {
          setIsSaved(true)
        }
      }
    } catch {}
    setIsLoading(false)
  }

  function setStudentStatus(studentId: string, status: Status) {
    setRecords((prev) =>
      prev.map((r) => (r.studentId === studentId ? { ...r, status } : r)),
    )
    setIsSaved(false)
  }

  function markAllPresent() {
    setRecords((prev) => prev.map((r) => ({ ...r, status: 'present' as Status })))
    setIsSaved(false)
  }

  async function handleSave() {
    setIsSaving(true)
    const attendanceRecords = records
      .filter((r) => r.status !== null)
      .map((r) => ({
        studentId: r.studentId,
        status: r.status!,
      }))

    try {
      const res = await fetch(apiUrl('/api/attendance'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, records: attendanceRecords }),
      })
      const data = await res.json()
      if (data.success) {
        setIsSaved(true)
        showToast('บันทึกการเช็คชื่อแล้ว')
      }
    } catch {
      showToast('เกิดข้อผิดพลาด')
    }
    setIsSaving(false)
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  const checkedCount = records.filter((r) => r.status !== null).length
  const absentStudents = records.filter((r) => r.status === 'absent')
  const presentCount = records.filter((r) => r.status === 'present').length
  const absentCount = absentStudents.length
  const leaveCount = records.filter((r) => r.status === 'leave').length

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto max-w-lg">
          <h1 className="text-lg font-bold text-gray-900">
            {isSaved ? '✓ บันทึกแล้ว' : 'เช็คชื่อ'} · {formatFullThaiDate(new Date())}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        {/* Summary */}
        <p className="mb-2 text-sm text-gray-500">
          เช็คแล้ว {checkedCount}/{records.length}
          {isSaved && (
            <span className="ml-2">
              · มา {presentCount} · ขาด {absentCount} · ลา {leaveCount}
            </span>
          )}
        </p>

        {/* Progress bar */}
        <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-primary-500 transition-all"
            style={{ width: `${records.length > 0 ? (checkedCount / records.length) * 100 : 0}%` }}
          />
        </div>

        {/* Post-save: absent notification */}
        {isSaved && absentStudents.length > 0 && (
          <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3">
            <p className="mb-2 text-sm font-medium text-orange-700">
              ขาดโดยไม่แจ้ง {absentStudents.length} คน
            </p>
            {absentStudents.map((s) => (
              <p key={s.studentId} className="text-sm text-orange-600">
                ! {s.studentNumber} · {s.prefix}{s.fullName}
                {s.nickname && ` (${s.nickname})`}
              </p>
            ))}
            <button
              onClick={async () => {
                setIsSendingNotify(true)
                try {
                  const res = await fetch(apiUrl(`/api/notifications/attendance/${date}/notify`), {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      studentIds: absentStudents.map((s) => s.studentId),
                    }),
                  })
                  const data = await res.json()
                  showToast(data.message || 'ส่งแจ้งเตือนแล้ว')
                } catch {
                  showToast('เกิดข้อผิดพลาด')
                }
                setIsSendingNotify(false)
              }}
              disabled={isSendingNotify}
              className="mt-2 w-full rounded-lg bg-orange-500 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isSendingNotify
                ? 'กำลังส่ง...'
                : `📣 แจ้งผู้ปกครอง ${absentStudents.length} คน`}
            </button>
          </div>
        )}

        {/* Mark all present shortcut */}
        {!isSaved && (
          <button
            onClick={markAllPresent}
            className="mb-3 w-full rounded-lg border border-green-200 bg-green-50 py-2 text-sm font-medium text-green-700"
          >
            ✓ ทำเครื่องหมาย "มา" ทุกคน
          </button>
        )}

        {/* Student list */}
        <div className="space-y-1">
          {records.map((r) => (
            <div
              key={r.studentId}
              className="flex items-center justify-between rounded-lg bg-white px-3 py-2.5 shadow-sm"
            >
              <span className="text-sm text-gray-900">
                {r.studentNumber} · {r.prefix}{r.fullName}
                {r.nickname && <span className="text-gray-400"> ({r.nickname})</span>}
              </span>
              <div className="flex overflow-hidden rounded-lg border border-gray-200">
                {(['present', 'absent', 'leave'] as Status[]).map((status) => {
                  const labels: Record<Status, string> = {
                    present: 'มา',
                    absent: 'ขาด',
                    leave: 'ลา',
                  }
                  const colors: Record<Status, string> = {
                    present: 'bg-green-500 text-white',
                    absent: 'bg-red-500 text-white',
                    leave: 'bg-amber-400 text-white',
                  }
                  const isActive = r.status === status
                  return (
                    <button
                      key={status}
                      onClick={() => setStudentStatus(r.studentId, status)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive ? colors[status] : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {labels[status]}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Save button */}
        {!isSaved && (
          <button
            onClick={handleSave}
            disabled={isSaving || checkedCount === 0}
            className="mt-4 w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isSaving ? 'กำลังบันทึก...' : 'บันทึกการเช็คชื่อ'}
          </button>
        )}
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="attendance" />
    </div>
  )
}
